import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import {
  MockGenieConfig,
  SetupMode,
  WizardStepId,
} from "@/types/mockups/genie-setup-v2";

import { getInitialConfig, isStepComplete } from "./composer";
import { wizardSteps } from "./data";
import { ModeSelector } from "./components/ModeSelector";
import { Step1Job } from "./components/Step1Job";
import { Step2Personality } from "./components/Step2Personality";
import { Step3Playbooks } from "./components/Step3Playbooks";
import { Step4Knowledge } from "./components/Step4Knowledge";
import { Step5Preview } from "./components/Step5Preview";
import { Step6Review } from "./components/Step6Review";
import { TypeSetup } from "./components/TypeSetup";
import { VoiceSetup } from "./components/VoiceSetup";

interface GenieSetupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: SetupMode | null;
}

type Screen = "mode" | "wizard" | "type" | "voice" | "deployed";

const screenForMode = (mode: SetupMode | null | undefined): Screen => {
  if (mode === "click") return "wizard";
  if (mode === "type") return "type";
  if (mode === "voice") return "voice";
  return "mode";
};

export const GenieSetupDrawer = ({ isOpen, onClose, initialMode }: GenieSetupDrawerProps) => {
  const [screen, setScreen] = useState<Screen>(screenForMode(initialMode));
  const [config, setConfig] = useState<MockGenieConfig>(getInitialConfig());
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (isOpen) setScreen(screenForMode(initialMode));
  }, [isOpen, initialMode]);

  const currentStepId: WizardStepId = wizardSteps[stepIndex]?.id ?? "job";
  const isLast = stepIndex === wizardSteps.length - 1;
  const stepDone = isStepComplete(currentStepId, config);

  const reset = () => {
    setScreen("mode");
    setConfig(getInitialConfig());
    setStepIndex(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleModeSelect = (mode: SetupMode) => {
    if (mode === "click") setScreen("wizard");
    if (mode === "type") setScreen("type");
    if (mode === "voice") setScreen("voice");
  };

  const handleNext = () => {
    if (isLast) {
      setScreen("deployed");
      return;
    }
    setStepIndex((i) => Math.min(wizardSteps.length - 1, i + 1));
  };

  const handleBack = () => {
    if (stepIndex === 0) {
      setScreen("mode");
      return;
    }
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const jumpToStep = (id: WizardStepId) => {
    const idx = wizardSteps.findIndex((s) => s.id === id);
    if (idx >= 0) setStepIndex(idx);
  };

  const handleTypeComplete = (filledConfig: MockGenieConfig) => {
    setConfig(filledConfig);
    setScreen("wizard");
    const reviewIdx = wizardSteps.findIndex((s) => s.id === "review");
    setStepIndex(reviewIdx >= 0 ? reviewIdx : 0);
  };

  const renderStep = () => {
    switch (currentStepId) {
      case "job":
        return <Step1Job config={config} onChange={setConfig} />;
      case "personality":
        return <Step2Personality config={config} onChange={setConfig} />;
      case "playbooks":
        return <Step3Playbooks config={config} onChange={setConfig} />;
      case "knowledge":
        return <Step4Knowledge config={config} onChange={setConfig} />;
      case "preview":
        return <Step5Preview config={config} onJumpToStep={jumpToStep} />;
      case "review":
        return <Step6Review config={config} onJumpToStep={jumpToStep} />;
      default:
        return null;
    }
  };

  const renderBody = () => {
    if (screen === "mode") {
      return <ModeSelector onSelect={handleModeSelect} />;
    }
    if (screen === "type") {
      return <TypeSetup onBack={() => setScreen("mode")} onComplete={handleTypeComplete} />;
    }
    if (screen === "voice") {
      return (
        <VoiceSetup
          onBack={() => setScreen("mode")}
          onSwitchToClick={() => setScreen("wizard")}
        />
      );
    }
    if (screen === "deployed") {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-genie-primary/10 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-genie-primary" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#222B45] mb-3">
            Your genie is ready
          </h2>
          <p className="text-sm md:text-base text-[#64748B] mb-8 max-w-md">
            This is a mockup — nothing was actually deployed. In the real flow your genie would
            now be live and ready to talk to customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={reset}>
              Set up another
            </Button>
            <Button
              onClick={handleClose}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
            >
              Done
            </Button>
          </div>
        </div>
      );
    }

    // wizard
    return (
      <div className="flex flex-col gap-6 pb-32">
        <WizardStepHeader currentStepId={currentStepId} onStepClick={jumpToStep} config={config} />
        <div>{renderStep()}</div>
      </div>
    );
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Set up a new genie"
      description="Answer a few questions and we'll configure everything for you. Switch setup modes anytime."
    >
      <div className="pb-2">{renderBody()}</div>

      {/* Sticky footer for wizard nav */}
      {screen === "wizard" && (
        <div className="sticky bottom-0 left-0 right-0 -mx-8 px-8 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
          <Button variant="outline" onClick={handleBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            {stepIndex === 0 ? "Back to setup mode" : "Back"}
          </Button>

          <div className="hidden md:block text-xs text-gray-500">
            Step {stepIndex + 1} of {wizardSteps.length} ·{" "}
            {stepDone ? (
              <span className="text-genie-primary font-medium">Ready to continue</span>
            ) : (
              <span>Pick at least one option</span>
            )}
          </div>

          <Button
            onClick={handleNext}
            disabled={!stepDone && !isLast}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 transition-all duration-200 disabled:opacity-50 gap-2"
          >
            {isLast ? "Deploy genie" : "Continue"}
            {!isLast && <ArrowRight className="w-4 h-4" />}
          </Button>
        </div>
      )}
    </CoreDrawer>
  );
};

const WizardStepHeader = ({
  currentStepId,
  onStepClick,
  config,
}: {
  currentStepId: WizardStepId;
  onStepClick: (id: WizardStepId) => void;
  config: MockGenieConfig;
}) => {
  const currentIndex = wizardSteps.findIndex((s) => s.id === currentStepId);
  const currentStep = wizardSteps[currentIndex];

  return (
    <div className="space-y-5">
      {/* Step pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
        {wizardSteps.map((step, idx) => {
          const isActive = step.id === currentStepId;
          const isPast = idx < currentIndex || isStepComplete(step.id, config);
          return (
            <div key={step.id} className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => onStepClick(step.id)}
                type="button"
                className="flex items-center gap-2 group"
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                    isActive
                      ? "bg-genie-primary text-white ring-4 ring-genie-primary/20"
                      : isPast
                        ? "bg-genie-primary/10 text-genie-primary"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {isPast && !isActive ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                </div>
                <span
                  className={`text-sm font-medium whitespace-nowrap ${
                    isActive
                      ? "text-[#222B45]"
                      : isPast
                        ? "text-gray-700"
                        : "text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
              </button>
              {idx < wizardSteps.length - 1 && (
                <div
                  className={`w-6 h-px ${isPast ? "bg-genie-primary/30" : "bg-gray-200"}`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Step question */}
      {currentStep && (
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#222B45] leading-tight">
            {currentStep.question}
          </h2>
          <p className="text-sm md:text-base text-[#64748B] mt-2">{currentStep.description}</p>
        </div>
      )}
    </div>
  );
};
